import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, MagnifyingGlass, X, Wallet } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";

const fmt = (n) => new Intl.NumberFormat("es-AR", { minimumFractionDigits: 2 }).format(n || 0);
const empty = { name: "", phone: "", email: "", document: "", address: "", notes: "" };

export default function Customers() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [query, setQuery] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const { data } = await api.get("/customers");
    setCustomers(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = customers.filter((c) => {
    const q = query.toLowerCase();
    return !q || c.name.toLowerCase().includes(q) || c.phone?.includes(q) || c.email?.toLowerCase().includes(q) || c.document?.includes(q);
  });

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/customers/${editing}`, form);
      else await api.post("/customers", form);
      toast.success(editing ? "Cliente actualizado" : "Cliente creado");
      setShowForm(false); setForm(empty); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="customers-page">
      <header className="mb-6 flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-turquoise font-mono mb-1">Personas</p>
          <h1 className="text-3xl md:text-4xl font-heading font-bold">Clientes</h1>
          <p className="text-zinc-400 mt-1 text-sm">{customers.length} cliente(s) registrados</p>
        </div>
        <button onClick={() => { setEditing(null); setForm(empty); setShowForm(true); }}
          data-testid="customer-new-btn"
          className="bg-brand-gradient text-black font-semibold px-5 py-2.5 rounded-md flex items-center gap-2 shadow-cyan-glow">
          <Plus size={18} weight="bold" /> Nuevo cliente
        </button>
      </header>

      <div className="relative flex-1 max-w-md mb-4">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar..."
          className="w-full bg-[#0F1115] border border-white/10 rounded-md pl-10 pr-4 py-2 text-sm" />
      </div>

      <div className="glass-card rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/5">
              <tr>
                <th className="text-left p-4 font-mono">Cliente</th>
                <th className="text-left p-4 font-mono">Contacto</th>
                <th className="text-left p-4 font-mono">Documento</th>
                <th className="text-right p-4 font-mono">Billetera</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="5" className="p-12 text-center text-zinc-500">Sin clientes</td></tr>
              ) : filtered.map((c) => (
                <tr key={c.customer_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="p-4 font-medium">{c.name}</td>
                  <td className="p-4 text-zinc-400 text-xs font-mono">
                    {c.phone && <div>{c.phone}</div>}
                    {c.email && <div className="text-zinc-500">{c.email}</div>}
                  </td>
                  <td className="p-4 font-mono text-xs text-zinc-500">{c.document || "—"}</td>
                  <td className="p-4 text-right">
                    <button
                      onClick={() => navigate(`/billeteras?customer=${c.customer_id}`)}
                      className={`inline-flex items-center gap-1.5 font-mono hover:underline ${c.wallet_balance < 0 ? "text-red-400" : c.wallet_balance > 0 ? "text-turquoise" : "text-zinc-500"}`}>
                      <Wallet size={14} /> {c.wallet_balance < 0 ? "-" : ""}$ {fmt(Math.abs(c.wallet_balance))}
                    </button>
                  </td>
                  <td className="p-4 text-right">
                    <button onClick={() => { setEditing(c.customer_id); setForm(c); setShowForm(true); }}
                      className="text-zinc-400 hover:text-turquoise mr-3" data-testid={`edit-customer-${c.customer_id}`}>
                      <PencilSimple size={16} />
                    </button>
                    {isAdmin && (
                      <button onClick={async () => {
                        if (!confirm("¿Eliminar cliente?")) return;
                        await api.delete(`/customers/${c.customer_id}`);
                        toast.success("Eliminado"); load();
                      }} className="text-zinc-400 hover:text-red-400">
                        <Trash size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="glass-card rounded-xl w-full max-w-xl" data-testid="customer-form-modal">
            <div className="flex items-center justify-between p-5 border-b border-white/5">
              <h3 className="font-heading text-lg font-semibold">{editing ? "Editar" : "Nuevo"} cliente</h3>
              <button onClick={() => setShowForm(false)}><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-wider text-zinc-400 font-mono">Nombre *</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="customer-name-input"
                  className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-400 font-mono">Teléfono</label>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-400 font-mono">Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-400 font-mono">Documento (DNI/CUIT)</label>
                <input value={form.document} onChange={(e) => setForm({ ...form, document: e.target.value })}
                  className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm font-mono" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-400 font-mono">Dirección</label>
                <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-wider text-zinc-400 font-mono">Notas</label>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows="2"
                  className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm" />
              </div>
              <div className="md:col-span-2 flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 bg-white/5 border border-white/10 py-2.5 rounded-md">Cancelar</button>
                <button type="submit" data-testid="customer-save-btn" className="flex-1 bg-brand-gradient text-black font-semibold py-2.5 rounded-md">
                  {editing ? "Actualizar" : "Crear"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
